import { useCallback, useEffect, useState } from "react";
import { useTenant } from "../../Contexts/TenantContext";
import { productService } from "../../Api/Services/productService";
import { GetProduct } from "../../Models/Product";
import { SimplifiedUploadedFile } from "../../Models/UploadedFile";
import { toast } from "react-toastify";
import FileUploader from "../FileUploader/FileUploader";
import Modal from "../common/Modal/Modal";

type ProductMediaKey =
  | "productImageOne"
  | "productImageTwo"
  | "productImageThree"
  | "productImageFour"
  | "productDocumentOne"
  | "productDocumentTwo";

interface AdminProductFilesModalProps {
  productId: string | null;
  onClose: () => void;
}

const AdminProductFilesModal = ({ productId, onClose }: AdminProductFilesModalProps) => {
  const { tenantPreference } = useTenant();
  const enableProductComponents = tenantPreference?.enableProductComponents ?? true;

  const [product, setProduct] = useState<GetProduct | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const loadProduct = useCallback(async (id: string, signal: AbortSignal) => {
    setIsLoading(true);
    try {
      const data = await productService.get(id);
      if (signal.aborted) {
        return;
      }
      if (!data) {
        toast.error("Produto não encontrado.");
        onClose();
        return;
      }
      setProduct(data);
    } catch {
      if (signal.aborted) {
        return;
      }
      toast.error("Erro ao carregar produto.");
      onClose();
    } finally {
      if (!signal.aborted) {
        setIsLoading(false);
      }
    }
  }, [onClose]);

  useEffect(() => {
    if (!productId) {
      setProduct(null);
      return;
    }
    const controller = new AbortController();
    loadProduct(productId, controller.signal);
    return () => controller.abort();
  }, [productId, loadProduct]);

  const handleUploadComplete = (key: ProductMediaKey, file: SimplifiedUploadedFile) => {
    setProduct(prev => prev ? { ...prev, [key]: file } : prev);
  };

  const handleDeleteComplete = (key: ProductMediaKey) => {
    setProduct(prev => prev ? { ...prev, [key]: null } : prev);
  };

  return (
    <Modal
      isOpen={productId !== null}
      onClose={onClose}
      maxWidth="max-w-3xl"
    >
      <h2 className="text-lg font-semibold mb-6">{"Editar arquivos"}</h2>
      {isLoading || !product ? (
        <div className="flex justify-center py-8">
          <div className="h-8 w-8 border-4 border-primary-600 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <div className="space-y-6">
          <div>
            <h3 className="text-lg mb-3">{"Imagens"}</h3>
            <div className="grid grid-cols-2 gap-5">
              <FileUploader
                label={"Imagem principal"}
                context="product"
                purpose="ProductMainImage"
                accept=".jpg,.jpeg,.png"
                referenceId={product.id}
                file={product.productImageOne ?? null}
                onUploadComplete={file => handleUploadComplete("productImageOne", file)}
                onDeleteComplete={() => handleDeleteComplete("productImageOne")}
              />
              <FileUploader
                label={"Imagem 2"}
                context="product"
                purpose="ProductImage2"
                accept=".jpg,.jpeg,.png"
                referenceId={product.id}
                file={product.productImageTwo ?? null}
                onUploadComplete={file => handleUploadComplete("productImageTwo", file)}
                onDeleteComplete={() => handleDeleteComplete("productImageTwo")}
              />
              <FileUploader
                label={"Imagem 3"}
                context="product"
                purpose="ProductImage3"
                accept=".jpg,.jpeg,.png"
                referenceId={product.id}
                file={product.productImageThree ?? null}
                onUploadComplete={file => handleUploadComplete("productImageThree", file)}
                onDeleteComplete={() => handleDeleteComplete("productImageThree")}
              />
              <FileUploader
                label={"Imagem 4"}
                context="product"
                purpose="ProductImage4"
                accept=".jpg,.jpeg,.png"
                referenceId={product.id}
                file={product.productImageFour ?? null}
                onUploadComplete={file => handleUploadComplete("productImageFour", file)}
                onDeleteComplete={() => handleDeleteComplete("productImageFour")}
              />
            </div>
          </div>

          {enableProductComponents && (
            <div>
              <h3 className="text-lg mb-3">{"Documentos"}</h3>
              <div className="grid grid-cols-2 gap-5">
                <FileUploader
                  label={"Ficha Técnica / Desenho Técnico"}
                  context="product"
                  purpose="ProductDocument1"
                  accept=".pdf,.jpg,.jpeg,.png"
                  referenceId={product.id}
                  file={product.productDocumentOne ?? null}
                  onUploadComplete={file => handleUploadComplete("productDocumentOne", file)}
                  onDeleteComplete={() => handleDeleteComplete("productDocumentOne")}
                />
                <FileUploader
                  label={"Termo de garantia"}
                  context="product"
                  purpose="ProductDocument2"
                  accept=".pdf,.jpg,.jpeg,.png"
                  referenceId={product.id}
                  file={product.productDocumentTwo ?? null}
                  onUploadComplete={file => handleUploadComplete("productDocumentTwo", file)}
                  onDeleteComplete={() => handleDeleteComplete("productDocumentTwo")}
                />
              </div>
            </div>
          )}

        </div>
      )}
    </Modal>
  );
};

export default AdminProductFilesModal;
